/**
 * ProductGallery Component
 * Image gallery with zoom and navigation
 * BP-08-04: Marketplace Frontend (Family)
 */

import React, { useState, useCallback } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  ZoomIn, 
  X,
  ImageIcon 
} from 'lucide-react';
import type { ProductGalleryProps } from '@/features/marketplace/types/marketplaceTypes';

export const ProductGallery: React.FC<ProductGalleryProps> = ({
  images,
  productName,
  initialIndex = 0,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  
  const hasImages = images.length > 0;
  const hasMultipleImages = images.length > 1;
  
  const goToPrevious = useCallback(() => {
    setSelectedIndex((prev) => 
      prev === 0 ? images.length - 1 : prev - 1
    );
  }, [images.length]);
  
  const goToNext = useCallback(() => {
    setSelectedIndex((prev) => 
      prev === images.length - 1 ? 0 : prev + 1
    );
  }, [images.length]);
  
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') goToPrevious();
    if (e.key === 'ArrowRight') goToNext();
    if (e.key === 'Escape') setIsZoomed(false);
  }, [goToPrevious, goToNext]);
  
  return (
    <div className="w-full" onKeyDown={handleKeyDown} tabIndex={0}>
      {/* Main Image */}
      <div className="
        relative aspect-[4/3] rounded-xl overflow-hidden
        bg-cream-dark mb-4
      ">
        {hasImages ? (
          <>
            <img
              src={images[selectedIndex]}
              alt={`${productName} - Image ${selectedIndex + 1}`}
              className="w-full h-full object-contain"
            />
            
            {/* Zoom button */}
            <button
              onClick={() => setIsZoomed(true)}
              className="
                absolute bottom-4 right-4
                p-2 rounded-lg
                bg-white/90 backdrop-blur-sm
                text-navy hover:bg-white
                transition-colors duration-200
                shadow-soft
              "
              aria-label="Zoom image"
            >
              <ZoomIn className="w-5 h-5" />
            </button>
            
            {/* Navigation arrows */}
            {hasMultipleImages && (
              <>
                <button
                  onClick={goToPrevious}
                  className="
                    absolute left-4 top-1/2 -translate-y-1/2
                    p-2 rounded-full
                    bg-white/90 backdrop-blur-sm
                    text-navy hover:bg-white
                    transition-colors duration-200
                    shadow-soft
                  "
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                
                <button
                  onClick={goToNext}
                  className="
                    absolute right-4 top-1/2 -translate-y-1/2
                    p-2 rounded-full
                    bg-white/90 backdrop-blur-sm
                    text-navy hover:bg-white
                    transition-colors duration-200
                    shadow-soft
                  "
                  aria-label="Next image"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
            
            {/* Image counter */}
            {hasMultipleImages && (
              <div className="
                absolute bottom-4 left-4
                px-3 py-1.5 rounded-full
                bg-navy/80 backdrop-blur-sm
                text-white text-sm font-medium
              ">
                {selectedIndex + 1} / {images.length}
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-16 h-16 text-warm-gray" />
          </div>
        )}
      </div>
      
      {/* Thumbnails */}
      {hasMultipleImages && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => setSelectedIndex(index)}
              className={`
                flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden
                border-2 transition-all duration-200
                ${index === selectedIndex 
                  ? 'border-sage ring-2 ring-sage/20' 
                  : 'border-transparent hover:border-sage/50'
                }
              `}
              aria-label={`View image ${index + 1}`}
              aria-current={index === selectedIndex ? 'true' : 'false'}
            >
              <img
                src={image}
                alt={`${productName} thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
      
      {/* Zoom Modal */}
      {isZoomed && hasImages && (
        <div 
          className="
            fixed inset-0 z-50
            bg-navy/95 backdrop-blur-sm
            flex items-center justify-center
            animate-fadeIn
          "
          onClick={() => setIsZoomed(false)}
        >
          <button
            onClick={() => setIsZoomed(false)}
            className="
              absolute top-4 right-4
              p-3 rounded-full
              bg-white/10 hover:bg-white/20
              text-white
              transition-colors duration-200
            "
            aria-label="Close zoom"
          >
            <X className="w-6 h-6" />
          </button>
          
          {hasMultipleImages && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                className="
                  absolute left-4 top-1/2 -translate-y-1/2
                  p-3 rounded-full
                  bg-white/10 hover:bg-white/20
                  text-white
                  transition-colors duration-200
                "
                aria-label="Previous image"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              
              <button
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                className="
                  absolute right-4 top-1/2 -translate-y-1/2
                  p-3 rounded-full
                  bg-white/10 hover:bg-white/20
                  text-white
                  transition-colors duration-200
                "
                aria-label="Next image"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}
          
          <img
            src={images[selectedIndex]}
            alt={`${productName} - Image ${selectedIndex + 1}`}
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          {hasMultipleImages && (
            <div className="
              absolute bottom-4 left-1/2 -translate-x-1/2
              px-4 py-2 rounded-full
              bg-white/10 backdrop-blur-sm
              text-white text-sm font-medium
            ">
              {selectedIndex + 1} / {images.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductGallery;

